/**
 * PhoneContactsModal - Browse native device contacts on Capacitor
 * 
 * Uses @capacitor-community/contacts to access the device address book.
 * Only available on mobile (iOS/Android). Lets user search and select
 * a contact's phone number to populate the dialpad.
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import {X,
  Search,
  Phone,
  User,
  BookUser,
  ShieldAlert} from 'lucide-react';
import {
  getContacts,
  searchContacts,
  type PhoneContact,
} from '@/lib/contactsUtils';

export interface PhoneContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectNumber: (number: string, contactName: string) => void;
  isDark?: boolean;
  primaryColor?: string;
}

export function PhoneContactsModal({
  isOpen,
  onClose,
  onSelectNumber,
  isDark = true,
  primaryColor = '#3b82f6',
}: PhoneContactsModalProps) {
  const [contacts, setContacts] = useState<PhoneContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedContact, setExpandedContact] = useState<string | null>(null);

  // Load contacts on mount
  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPermissionDenied(false);

    const result = await getContacts({ forceRefresh: false });

    if (result.success) {
      setContacts(result.contacts);
    } else {
      setError(result.error || 'Failed to load contacts');
      if (result.permissionDenied) {
        setPermissionDenied(true);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      queueMicrotask(() => {
        void loadContacts();
        setSearchQuery('');
        setExpandedContact(null);
      });
    }
  }, [isOpen, loadContacts]);

  // Search handler
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      const result = await getContacts();
      if (result.success) setContacts(result.contacts);
      return;
    }
    const result = await searchContacts(query);
    if (result.success) setContacts(result.contacts);
  }, []);

  // Select a phone number
  const handleSelect = useCallback((number: string, name: string) => {
    // Clean the number — remove spaces, dashes, parentheses
    const cleaned = number.replace(/[\s\-()]/g, '');
    onSelectNumber(cleaned, name);
    onClose();
  }, [onSelectNumber, onClose]);

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-20 p-4">
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl w-full max-w-sm overflow-hidden max-h-[80vh] flex flex-col`}>
        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center justify-between flex-shrink-0 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold flex items-center gap-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            <BookUser className="w-5 h-5" />
            Phone Contacts
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
          >
            <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        {/* Search */}
        <div className={`px-4 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            isDark ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <Search className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search contacts..."
              className={`flex-1 bg-transparent text-sm outline-none ${
                isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <BrandedLoadingSpinner mode="compact" size="sm" primaryColor={primaryColor} showSurface={false} showProgressBar={false} />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Loading contacts...
              </p>
            </div>
          ) : permissionDenied ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 px-6">
              <ShieldAlert className="w-10 h-10 text-orange-400" />
              <p className={`text-sm text-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Contacts permission denied
              </p>
              <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Please enable contacts access in your device settings to use this feature.
              </p>
              <button
                onClick={loadContacts}
                className="mt-2 px-4 py-2 text-sm rounded-lg text-white transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                Try Again
              </button>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 px-6">
              <p className={`text-sm text-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {error}
              </p>
              <button
                onClick={loadContacts}
                className="mt-2 px-4 py-2 text-sm rounded-lg text-white transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                Retry
              </button>
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <User className={`w-10 h-10 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {searchQuery ? 'No contacts found' : 'No contacts with phone numbers'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {contacts.map((contact) => (
                <div key={contact.id}>
                  <button
                    onClick={() => {
                      if (contact.phoneNumbers.length === 1) {
                        // Single number — select directly
                        handleSelect(contact.phoneNumbers[0].number, contact.name);
                      } else {
                        // Multiple numbers — expand to show options
                        setExpandedContact(
                          expandedContact === contact.id ? null : contact.id
                        );
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${
                      isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Avatar */}
                    {contact.photoThumbnail ? (
                      // eslint-disable-next-line @next/next/no-img-element -- device contacts expose base64 thumbnails which are rendered directly in the mobile contacts picker
                      <img
                        src={`data:image/jpeg;base64,${contact.photoThumbnail}`}
                        alt={contact.name}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDark ? 'bg-gray-600' : 'bg-gray-200'
                      }`}>
                        <User className={`w-5 h-5 ${isDark ? 'text-gray-300' : 'text-gray-500'}`} />
                      </div>
                    )}

                    {/* Name & number preview */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm truncate ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {contact.name}
                      </p>
                      <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {contact.phoneNumbers[0]?.number}
                        {contact.phoneNumbers.length > 1 && ` (+${contact.phoneNumbers.length - 1})`}
                      </p>
                    </div>

                    {/* Quick call icon for single-number contacts */}
                    {contact.phoneNumbers.length === 1 && (
                      <Phone className="w-4 h-4 text-green-400 flex-shrink-0" />
                    )}
                  </button>

                  {/* Expanded number list for multi-number contacts */}
                  {expandedContact === contact.id && contact.phoneNumbers.length > 1 && (
                    <div className={`px-4 pb-2 ${isDark ? 'bg-gray-750' : 'bg-gray-50'}`}>
                      {contact.phoneNumbers.map((phone, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSelect(phone.number, contact.name)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                            isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 text-green-400" />
                            <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {phone.number}
                            </span>
                          </div>
                          <span className={`text-xs capitalize ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {phone.type}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer count */}
        {!loading && !error && contacts.length > 0 && (
          <div className={`px-4 py-2 border-t text-center flex-shrink-0 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {contacts.length} contact{contacts.length !== 1 ? 's' : ''} with phone numbers
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhoneContactsModal;
